<script setup lang="ts">
import { computed, ref } from 'vue'
import { sleep } from '@bc-library/common'
import type { size as sizes } from '@/services/application/constantes'
import { bgColors } from '@/services/application/constantes'
import BButton from '@/components/common/bButton.vue'
import BDropdown from '@/components/common/bDropdown.vue'
import BConfirm from '@/components/common/bConfirm.vue'
import { imageUrlToBase64 } from '@/services/application/index'
import IconArrowDown from '~icons/ic/sharp-keyboard-arrow-down'
import SolarCloudUploadLineDuotone from '~icons/solar/cloud-upload-line-duotone'
import { recruitmentApi } from '@/services/api'
import { retriveFile } from '@/services/application'
import LineMdDownloadingLoop from '~icons/line-md/downloading-loop'
import LineMdCircleToConfirmCircleTwotoneTransition from '~icons/line-md/circle-to-confirm-circle-twotone-transition'
import { useUserStore } from '@/config/store/userStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = withDefaults(defineProps<{
  type?: 'CV' | 'CL'
  display?: string
  size?: keyof typeof sizes
  dataCy?: string
}>(), {
  type: 'CV',
  display: 'CV',
  size: 'dense',
  dataCy: 'candidate-document',
})

const emits = defineEmits<{
  openViewer: [file: { filePath: string, fileType: string }, fileName: string]
}>()

const scale = ref(1.25)

const fileState = ref<null | 'loading' | 'success' | 'error'>(null)

const recruitmentProcess = defineModel<{
  _id: string
  resumePath: {
    filePath: string
    fileType: string
  }
  coverLetterPath: {
    filePath: string
    fileType: string
  }
  candidate: {
    _id: string
    firstname: string
    lastname: string
  }
}>()

const fileToDisplay = computed(() => {
  if (props.type === 'CV')
    return recruitmentProcess.value.resumePath
  return recruitmentProcess.value.coverLetterPath
})

const confirmModal = ref({
  deleteDocument: {
    isOpen: false,
    message: computed(() => t('modules.recruitment.screening.candidateDocuments.confirmDelete')),
    confirmCallback: () => {
      confirmModal.value.deleteDocument.isOpen = false
      recruitmentApi.deleteCandidateFile({ processId: recruitmentProcess.value._id, type: props.type.toLowerCase() }).promise.then((response) => {
        if (props.type === 'CV')
          recruitmentProcess.value.resumePath = response.file
        else
          recruitmentProcess.value.coverLetterPath = response.file
      })
    },
  },
})

const extention = computed(() => fileToDisplay.value.filePath.split('.').pop().split('?')[0])
const fileName = computed(() => `${props.display}-${recruitmentProcess.value.candidate.firstname}-${recruitmentProcess.value.candidate.lastname}`)
const triggerDownload = async () => {
  const base64 = await imageUrlToBase64(fileToDisplay.value.filePath)
  const linkSource = base64 as string
  const downloadLink = document.createElement('a')
  downloadLink.href = linkSource
  downloadLink.download = `${fileName.value}.${extention.value}`
  downloadLink.click()
  downloadLink.remove()
}

const isUploaded = computed(() => {
  if (props.type === 'CV')
    return !!recruitmentProcess.value.resumePath.filePath
  return !!recruitmentProcess.value.coverLetterPath.filePath
})

const color = computed(() => fileState.value === 'loading' ? 'warning-op' : fileState.value === 'error' ? 'error-op' : isUploaded.value ? 'success-op' : 'base')

const uploadFile = async (file) => {
  try {
    fileState.value = 'loading'
    const formData = new FormData()
    formData.append('file', file)
    const response = await recruitmentApi.uploadCandidateFile({ processId: recruitmentProcess.value._id, type: props.type.toLowerCase(), data: formData }).promise

    if (props.type === 'CV')
      recruitmentProcess.value.resumePath = response.file
    else
      recruitmentProcess.value.coverLetterPath = response.file

    fileState.value = 'success'
    sleep(2000).then(() => {
      fileState.value = null
    })
  }
  catch (err) {
    console.log('Failed to upload file candidate', err)
    fileState.value = 'error'
    sleep(2000).then(() => {
      fileState.value = null
    })
  }
}
const clickButton = async () => {
  if (isUploaded.value) {
    emits('openViewer', fileToDisplay.value, fileName.value)
    return
  }

  const file = await retriveFile({ multiple: false }) as File[] | null
  if (!file?.length)
    return
  uploadFile(file[0])
}

// The menu and the delete confirmation are mounted at their first use: each row of the screening list has two
// buttons, which otherwise each kept a popper, a modal and its global listener
const isMenuMounted = ref(false)
const isMenuOpen = ref(false)
const isDeleteConfirmMounted = ref(false)
const openMenu = () => {
  isMenuMounted.value = true
  isMenuOpen.value = true
}

const userStore = useUserStore()
const dropdownItems = computed(() => {
  const items: { _id: string, bgColor: 'base' | 'error', click: () => void, display: string }[] = [{ _id: 'download', bgColor: 'base', click: () => { triggerDownload() }, display: t('modules.recruitment.screening.candidateDocuments.download') }]
  if (userStore.user.profile.modules.recruitment.processInterviews.editCandidateInformations)
    items.push(
      { _id: 'delete', bgColor: 'error', click: () => { isDeleteConfirmMounted.value = true; confirmModal.value.deleteDocument.isOpen = true }, display: t('modules.recruitment.screening.candidateDocuments.delete') }
    )
  return items
})

</script>

<template>
  <div class="flex h-fit" @click.prevent @click.stop>
    <BButton :bg="color" :size="size" :class="{ ' rounded-r-none': isUploaded && fileState === null }"
      class="flex items-center px-4" @click="clickButton" :data-cy="`candidate-document-${props.dataCy}-button`">
      {{ props.display }}
      <transition name="fade" mode="out-in">
        <SolarCloudUploadLineDuotone v-if="!isUploaded && fileState === null" />
        <LineMdDownloadingLoop v-else-if="fileState === 'loading'" />
        <LineMdCircleToConfirmCircleTwotoneTransition v-else-if="fileState === 'success'" />
      </transition>
    </BButton>
    <div v-if="isUploaded && fileState === null" :class="[bgColors[color]]" class="border-l border-base rounded-r-lg">
      <BDropdown v-if="isMenuMounted" v-model:is-open="isMenuOpen" position="bottom-end" :items="dropdownItems" @click.prevent @click.stop>
        <template #triggerContent>
          <span class="px-2">
            <IconArrowDown class="cursor-pointer" />
          </span>
        </template>
      </BDropdown>
      <span v-else class="flex h-full items-center px-2" data-cy="candidate-document-menu" @click="openMenu">
        <IconArrowDown class="cursor-pointer" />
      </span>
    </div>
    <BConfirm v-if="isDeleteConfirmMounted" v-model:is-open="confirmModal.deleteDocument.isOpen" :message="confirmModal.deleteDocument.message"
      :confirm-callback="confirmModal.deleteDocument.confirmCallback" />
  </div>
</template>

<style scoped>
.v-popper--theme-dropdown{
  height: 100%;
}
.v-popper--theme-dropdown span {
  height: 100%;
  display: flex;
  align-items: center;
}
</style>
